(ns ductile.ui.damages
  (:require [applied-science.js-interop :as j]
            [clojure.string :as str]
            [ductile.ui.components :as ui]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.entry-protocol :as entry-protocol]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.util :as util]
            [nextjournal.clerk.render.hooks :as hooks]))

(def default-outside-regions
  [{:damage/region "Spoiler"}
   {:damage/region "Stoßstange vorne" :damage/kind "Steinschlag"}
   {:damage/region "Frontgrill"}
   {:damage/region "Scheinwerfer links"}
   {:damage/region "Scheinwerfer rechts"}
   {:damage/region "Motorhaube" :damage/kind "Steinschlag"}
   {:damage/region "Seitenteil vorne links"}
   {:damage/region "Seitenteil vorne rechts"}
   {:damage/region "Türe vorne links"}
   {:damage/region "Türe vorne rechts"}
   {:damage/region "Türe hinten links"}
   {:damage/region "Türe hinten rechts"}
   {:damage/region "Seitenteil hinten links"}
   {:damage/region "Seitenteil hinten rechts"}
   {:damage/region "Frontscheibe" :damage/kind "Steinschlag"}
   {:damage/region "Dach"}
   {:damage/region "Seitenscheibe vorne links"}
   {:damage/region "Seitenscheibe vorne rechts"}
   {:damage/region "Seitenscheibe hinten links"}
   {:damage/region "Seitenscheibe hinten rechts"}
   {:damage/region "Dreiecksfenster hinten links"}
   {:damage/region "Dreiecksfenster hinten rechts"}
   {:damage/region "Heckscheibe"}
   {:damage/region "Heckleuchte links"}
   {:damage/region "Heckleuchte rechts"}
   {:damage/region "Reifen vorne links"}
   {:damage/region "Felge vorne links"}
   {:damage/region "Reifen vorne rechts"}
   {:damage/region "Felge vorne rechts"}
   {:damage/region "Reifen hinten links"}
   {:damage/region "Felge hinten links"}
   {:damage/region "Reifen hinten rechts"}
   {:damage/region "Felge hinten rechts"}
   {:damage/region "Schweller links"}
   {:damage/region "Schweller rechts"}
   {:damage/region "Schiebedach"}
   {:damage/region "Tankklappe"}
   {:damage/region "Heckdeckel"}
   {:damage/region "Heckblech"}
   {:damage/region "Stoßstange hinten"}
   {:damage/region "Heckschürze"}
   {:damage/region "Scheibenwischer links"}
   {:damage/region "Scheibenwischer rechts"}
   {:damage/region "Scheibenwischer hinten"}
   {:damage/region "Ausstellfenster links vorne"}
   {:damage/region "Ausstellfenster rechts vorne"}
   {:damage/region "Außenspiegel links"}
   {:damage/region "Außenspiegel rechts"}
   {:damage/region "Zierleiste"}
   {:damage/region "Radkappen"}
   {:damage/region "Auspuffanlage"}
   {:damage/region "Bodengruppe"}
   {:damage/region "Batterie"}
   {:damage/region "Katalysator"}
   {:damage/region "Achse"}
   {:damage/region "Getriebe"}
   {:damage/region "Antenne"}])

(def default-inside-regions
  [{:damage/region "Vordersitze"}
   {:damage/region "Rücksitze"}
   {:damage/region "Innenverkleidung"}
   {:damage/region "Teppichboden"}
   {:damage/region "Dachhimmel"}
   {:damage/region "Kofferraum/Ladefläche"}
   {:damage/region "Armaturentafel/Mittelkonsole"}
   {:damage/region "sonstiges"}])

(def default-outside-kinds
  [{:damage/kind "Kratzer" :damage/length nil :damage/unit :unit/cm}
   {:damage/kind "Steinschlag" :damage/length nil :damage/unit :unit/cm}
   {:damage/kind "Hagel"}
   {:damage/kind "Delle" :damage/diameter nil :damage/unit :unit/cm}
   {:damage/kind "Beule"}
   {:damage/kind "Bruch"}
   {:damage/kind "Lackschaden"}
   {:damage/kind "Verschmutzung"}
   {:damage/kind "Reifenschäden"}
   {:damage/kind "Defekt"}
   {:damage/kind "Fehlteil"}])

(def default-inside-kinds
  [{:damage/kind "Verschmutzung"}
   {:damage/kind "Beschädigung"}
   {:damage/kind "Riss" :damage/length nil :damage/unit :unit/cm}])

(def damage-details
  [:damage/diameter
   :damage/length])

(defn add-damage! [{:as props :keys [path kinds regions]} region-name extra-attrs]
  (let [region (first (filter (comp (partial = region-name) :damage/region) regions))
        damage (merge extra-attrs
                      (first kinds)
                      region)]
    (deframe/tx! [[:op/assoc-in [:current-route/transient ::damage-modal] (count @(deframe/!route-get-in path))]
                  [:op/assoc-in (into [:current-route/transient] path) (conj @(deframe/!route-get-in path []) damage)]])))

(defn remove-damage! [{:keys [path]} damage]
  (deframe/route-assoc-in! path (vec (remove #(= % (dissoc damage :ui/index)) @(deframe/!route-get-in path)))))

(defn select-region [{:as props :keys [regions]}]
  (let [!el (hooks/use-ref nil)]
    [:div.flex.justify-center
     (into [:select.p-3.rounded-lg.select-box
            {:ref !el
             :on-change (fn [event]
                          (let [region-name (.. event -target -value)]
                            (when (string? region-name)
                              (add-damage! props region-name {})
                              (j/assoc! @!el :value -1))))}
            [:option {:value -1} (str (tr "Select a region") "…")]]
           (map-indexed (fn [i {:damage/keys [region]}]
                          [:option {:value region} region]))
           regions)]))

(comment
  @(deframe/!route-get-in [:ductile.ui.workflow/recorded-attrs :vehicle/outside-damages]))

(defn select-damage-type [{:keys [path kinds]} damage]
  (let [damages @(deframe/!route-get-in path)
        damage-path (vec (concat path [(:ui/index damage)]))
        damage' (apply dissoc damage (concat [:damage/kind :damage/unit :ui/index] damage-details))]
    (into [:select.p-1.rounded-md.border.flex-auto.select-box
           {:on-change (fn [event]
                         (let [i (parse-long (.. event -target -value))]
                           (deframe/route-assoc-in!
                            damage-path
                            (if-let [kind (and (not (neg? i)) (nth kinds (parse-long (.. event -target -value))))]
                              (merge damage' (select-keys kind (concat [:damage/kind :damage/unit] damage-details)))
                              damage'))))
            :value (or (util/index-of (:damage/kind damage) (map :damage/kind kinds)) -1)}
           [:option {:value -1} (str (tr "Select kind") "…")]]
          (map-indexed (fn [i kind]
                         [:option {:value i} (:damage/kind kind)]))
          kinds)))

(defn damage-item [{:as props :keys [path]} {:as damage :keys [ui/index] :cas/keys [loading? multihash]}]
  (let [!el (hooks/use-ref nil)]
    #_(hooks/use-effect #(.scrollIntoView @!el #js {:behavior "smooth" :block "nearest"}))
    ^{:key (str "d2" index)}
    [:div.flex.gap-3.rounded-lg.bg-white.p-3
     {:ref !el}
     [:div.rounded-full.w-5.h-5.bg-red-500.text-white.flex.flex-shrink-0.items-center.justify-center.text-xs.leading-none
      (inc index)]
     [:div.flex-auto
      [:div.font-bold.mb-2.text-base
       {:class "-mt-0.5"}
       (:damage/region damage)]
      [:div.flex.gap-2.items-center
       [:div.text-sm.text-right.w-24 (str (tr "Kind") ":")]
       [select-damage-type props damage]]
      (when-let [detail (some #(when (contains? damage %) %) damage-details)]
        [:div.flex.gap-2.items-center.mt-1
         [:div.text-sm.text-right.flex-shrink-0.w-24 (str (tr detail) ":")]
         [ui/input {:type :text
                    :size 4
                    :value (or (damage detail) "")
                    :on-change (fn [event]
                                 (let [value (.. event -target -value)]
                                   (deframe/route-assoc-in!
                                    (vec (concat path [index detail]))
                                    (or (parse-long value) value))))
                    :right (when (contains? damage :damage/unit)
                             (tr (:damage/unit damage)))}]])
      [:div.flex.gap-2.mt-1
       [:div.text-sm.text-sm.text-right.w-24.mt-1 (str (tr "Description") ":")]
       [ui/input {:component :textarea
                  :size 4
                  :value (:damage/description damage "")
                  :on-change (fn [event]
                               (deframe/route-assoc-in! (vec (concat path [index :damage/description])) (.. event -target -value)))
                  :style {:min-height 50
                          :padding-top 5
                          :padding-bottom 5}}]]
      [:div.flex.gap-2.mt-1.pl-24
       (cond
         loading?
         [:div.ml-2.rounded-md.border.border-slate-400.shadow.flex.items-center.justify-center
          {:class "size-[40px]"}
          [ui/loading-indicator]]

         multihash
         [:div.ml-2.flex.items-center.gap-2
          [mobile/image-tile {:url (str "/cas/" multihash) :size 40}]
          [:div.text-indigo-600
           {:on-click #(deframe/route-assoc-in! (vec (conj path index)) (dissoc damage :file/mime-type :cas/multihash))}
           (tr "Remove photo")]]

         :else
         [mobile/capture-image
          {:on-capture (fn [base64-image]
                         (deframe/route-assoc-in! (conj path index :cas/loading?) true)
                         (deframe/call-lambda {:on-success (fn [cas-result]
                                                             (prn ::cas-result cas-result
                                                                  :path (conj path index))
                                                             (deframe/route-dissoc-in! (conj path index :cas/loading?))
                                                             (deframe/tx! [[:op/merge-at (into [:current-route/transient] (conj path index)) cas-result]]))
                                               :result-path [::_ignored]}
                                              'ductile.damages/upload-image
                                              {:image base64-image}))}
          [mobile/add-image-button {:class "ml-2"
                                    :label (str (tr "Add a photo of the damage") "…")}]])]
      [:div.flex.mt-3.justify-end.mr-2
       [:div.text-red-600.flex.items-center.gap-1.text-xs.cursor-pointer
        {:on-click #(remove-damage! props damage)}
        [ui/icon {:size 14} :trash]
        (tr "Remove damage")]]]]))

(defn selected-damages [{:as props :keys [path]}]
  (let [damages @(deframe/!route-get-in path)]
    [mobile/table
     {:inset? true
      :title (tr "Selected damages")
      :gap 2
      :items [(doall
               (map-indexed
                (fn [i damage]
                  ^{:key (str "damage" i)}
                  [damage-item props (assoc damage :ui/index i)])
                damages))]}]))

(defn eventToSvgCoordinates [event]
  (let [svg (.. event -target (closest "svg"))
        p (.createSVGPoint svg)]
    (j/assoc! p :x (.-clientX event) :y (.-clientY event))
    (let [tp (.matrixTransform p (.inverse (.getScreenCTM svg)))]
      [(js/Number (.toFixed (.-x tp) 2)) (js/Number (.toFixed (.-y tp) 2))])))

(defn on-select-diagram-region [{:as props :keys [path kinds regions]} event]
  (when-let [region-name (.. event -target (getAttribute "data-region"))]
    (add-damage! props region-name {:damage/position (eventToSvgCoordinates event)})))

(defn diagram-selector [{:as props :keys [diagram path damages]}]
  [entry-protocol/diagram-selector (assoc props
                                          :damages (if path
                                                     (filter :damage/position @(deframe/!route-get-in path))
                                                     damages)
                                          :on-click (when path (fn [event] (on-select-diagram-region props event))))])


(defn close-damage-modal! [] (deframe/route-dissoc! ::damage-modal))

(defn selector [{:as props :keys [diagram path]}]
  (let [select-region? @(deframe/!route-get ::select-region?)
        modal-damage-index @(deframe/!route-get ::damage-modal)
        modal-damage (when modal-damage-index (nth @(deframe/!route-get-in path) modal-damage-index nil))]
    (hooks/use-effect (fn []
                        (when (and modal-damage-index
                                   (not modal-damage)) ; damage deleted from modal
                          (close-damage-modal!)))
                      [modal-damage-index modal-damage])
    [:div
     (when modal-damage-index
       [mobile/modal {:class "w-[95vw]"
                      :on-close close-damage-modal!}
        [:div.p-2
         (when modal-damage
           [damage-item props (assoc modal-damage :ui/index modal-damage-index)])
         [mobile/button-full {:on-click close-damage-modal!} (tr "Save")]]])


     [:div.-mt-2.-mb-2
      [mobile/inline-prompt
       (if diagram
         (tr "Select damaged regions from the diagram or use the dropdown for additional regions")
         (tr "Please select a damaged region from the dropdown"))]]
     [select-region props]
     (when diagram
       [:div.flex.justify-center.mt-3
        [:div {:class "max-w-[400px]"}
         [diagram-selector props]]])
     (when (not-empty @(deframe/!route-get-in path))
       [selected-damages props])]))
